import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import EntrigyIntro from './EntrigyIntro';
import { buildUserData, dashboardPathFor } from '../auth/sessionUser';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;
const TWO_FACTOR_DEVICE_KEY = 'twoFactorDeviceToken';

// Enable cookie transmission
axios.defaults.withCredentials = true;

const PasswordStrengthMeter = ({ password }) => {
  const calculatePasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score += 20;
    if (pass.length >= 12) score += 10;
    if (/[a-z]/.test(pass)) score += 10;
    if (/[A-Z]/.test(pass)) score += 15;
    if (/\d/.test(pass)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) score += 15;
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/.test(pass)) score += 15;
    return score;
  };

  const getStrengthLabel = (score) => {
    if (score >= 80) return { label: 'Very Strong', color: 'bg-green-500' };
    if (score >= 60) return { label: 'Strong', color: 'bg-blue-500' };
    if (score >= 40) return { label: 'Medium', color: 'bg-yellow-500' };
    if (score >= 20) return { label: 'Weak', color: 'bg-orange-500' };
    return { label: 'Very Weak', color: 'bg-red-500' };
  };

  const strength = calculatePasswordStrength(password);
  const { label, color } = getStrengthLabel(strength);

  return (
    <div className="mt-2 text-left px-1">
      <div className="flex justify-between mb-1">
        <span className="text-[10px] text-white/50 uppercase tracking-wider">Password Strength:</span>
        <span className="text-[10px] text-white/50">{label}</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded">
        <div className={`h-1.5 rounded transition-all duration-300 ${color}`} style={{ width: `${strength}%` }} />
      </div>
      <div className="mt-2 text-[10px] text-white/40">
        <ul className="space-y-0.5">
          <li className={password.length >= 8 ? 'text-emerald-400' : ''}>• Minimum 8 characters</li>
          <li className={/[A-Z]/.test(password) ? 'text-emerald-400' : ''}>• At least one uppercase letter</li>
          <li className={/[a-z]/.test(password) ? 'text-emerald-400' : ''}>• At least one lowercase letter</li>
          <li className={/\d/.test(password) ? 'text-emerald-400' : ''}>• At least one number</li>
          <li className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-emerald-400' : ''}>• At least one special character</li>
        </ul>
      </div>
    </div>
  );
};

const LoginScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, logout } = useContext(UserContext);
  const [showLogin, setShowLogin] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isInviteRegister, setIsInviteRegister] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    recoveryEmail: '',
  });
  const [error, setError] = useState('');
  // "Sign in with Microsoft" (opt-in per company). The button exists only when some
  // company has single sign-on switched on — until then this page is unchanged.
  const [ssoAvailable, setSsoAvailable] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  // Server-enforced two-factor: a browser the person trusts skips the code next time (the
  // server hands back a device token; plan v2 §13.2 — the web never asked for one).
  const [rememberDevice, setRememberDevice] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [tempAccessToken, setTempAccessToken] = useState('');
  const [showRodoModal, setShowRodoModal] = useState(false);
  const [missingConsents, setMissingConsents] = useState([]);
  const [userDataTemp, setUserDataTemp] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneLogin, setIsPhoneLogin] = useState(false);
  const [phoneLoginEmail, setPhoneLoginEmail] = useState('');
  const [phoneLoginSessionId, setPhoneLoginSessionId] = useState(null);
  const [phoneLoginStatus, setPhoneLoginStatus] = useState(null); // 'waiting' | 'approved' | 'rejected' | 'expired'
  const [phoneLoginCountdown, setPhoneLoginCountdown] = useState(0);

  const handleIntroComplete = () => {
    setShowLogin(true);
  };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/sso/available/`)
      .then(({ data }) => { if (!cancelled) setSsoAvailable(data?.available === true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Back from Microsoft: the access token arrives in the URL fragment (never sent to
  // a server). Finish exactly like a password login, then clear it from the address.
  useEffect(() => {
    const hash = window.location.hash || '';
    if (hash.startsWith('#sso_access=')) {
      const token = decodeURIComponent(hash.slice('#sso_access='.length));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      if (token) handleSuccessfulLogin(token);
    } else if (searchParams.get('sso_error')) {
      setError('Microsoft sign-in did not work for this account. Use your password or ask your admin.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startMicrosoftSignIn = async () => {
    const email = (formData.username || '').trim();
    if (!email || !email.includes('@')) {
      setError('Enter your work e-mail above, then choose Microsoft.');
      return;
    }
    setSsoBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/sso/start/`, { email });
      window.location.assign(data.authorize_url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Microsoft sign-in is not available for this e-mail.');
      setSsoBusy(false);
    }
  };

  useEffect(() => {
    const token = searchParams.get('invite');
    if (!token) return;

    setInviteToken(token);
    axios.get(`${API_BASE_URL}/invite/${token}/`)
      .then(res => {
        setInviteData(res.data);
        setFormData(prev => ({ ...prev, email: res.data.email }));
        setIsInviteRegister(true);
        setIsRegistering(false);
      })
      .catch(err => {
        const msg = err.response?.data?.error || 'Invalid or expired invitation';
        setInviteError(msg);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccessMessage('');
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 10;
    if (/[a-z]/.test(password)) score += 10;
    if (/[A-Z]/.test(password)) score += 15;
    if (/\d/.test(password)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 15;
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password)) score += 15;
    return score;
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordStrength = calculatePasswordStrength(formData.password);
    if (passwordStrength < 40) {
      setError('Password is too weak. Please meet more security criteria.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/register/`, {
        email: formData.email,
        password: formData.password,
      });

      if (response.data.success) {
        setSuccessMessage(`Registration successful! Your username is: ${response.data.username}`);
        setFormData({ username: '', email: '', password: '', confirmPassword: '', recoveryEmail: '' });
        setTimeout(() => {
          setIsRegistering(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      // `error` first — the registration endpoint reports the password-policy
      // reason there; `message` alone hid it behind the generic fallback.
      setError(error.response?.data?.error || error.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordStrength = calculatePasswordStrength(formData.password);
    if (passwordStrength < 40) {
      setError('Password is too weak. Please meet more security criteria.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/register/`, {
        email: inviteData.email,
        password: formData.password,
        invite_token: inviteToken,
      });

      if (response.data.success) {
        setSuccessMessage(`Registration successful! You can now log in.`);
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        setTimeout(() => {
          setIsInviteRegister(false);
          setInviteToken(null);
          setInviteData(null);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setUserDataTemp(null);
    setTempAccessToken('');
    setMissingConsents([]);
    setShowRodoModal(false);
    setIsLoading(true);
    try {
      let deviceToken = null;
      try { deviceToken = localStorage.getItem(TWO_FACTOR_DEVICE_KEY); } catch (e) { /* storage blocked */ }
      const response = await axios.post(`${API_BASE_URL}/token/`, {
        username: formData.username,
        password: formData.password,
        ...(deviceToken ? { device_token: deviceToken } : {}),
      });

      setTempAccessToken(response.data.access);

      const verificationResponse = await axios.post(
        `${API_BASE_URL}/send-verification-email/`,
        {},
        { headers: { Authorization: `Bearer ${response.data.access}` } }
      );

      if (verificationResponse.data.twoFactorRequired) {
        setShowModal(true);
      } else {
        handleSuccessfulLogin(response.data.access);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setError('Too many login attempts. Please wait 30 seconds before trying again.');
      } else if (error.response?.status === 401) {
        setError('Invalid login credentials');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/recover-password/`, {
        email: formData.recoveryEmail,
      });
      setSuccessMessage('If this email exists in our system, you will receive password reset instructions shortly.');
      setTimeout(() => {
        setIsForgotPassword(false);
        setSuccessMessage('');
        setFormData({ ...formData, recoveryEmail: '' });
      }, 3000);
    } catch (error) {
      setError('Password recovery failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Phone Login via Push Notification
  const handlePhoneLoginRequest = async (e) => {
    e.preventDefault();
    if (!phoneLoginEmail || !phoneLoginEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/web-login-request/`, {
        email: phoneLoginEmail,
      });
      setPhoneLoginSessionId(response.data.session_id);
      setPhoneLoginStatus('waiting');
      setPhoneLoginCountdown(300);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Please wait before requesting again.');
      } else {
        setError('Failed to send login request. Try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for phone login status
  useEffect(() => {
    if (!phoneLoginSessionId || phoneLoginStatus !== 'waiting') return;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/auth/web-login-status/${phoneLoginSessionId}/`
        );
        const s = response.data.status;
        if (s === 'approved' && response.data.access) {
          setPhoneLoginStatus('approved');
          clearInterval(interval);
          handleSuccessfulLogin(response.data.access);
        } else if (s === 'rejected') {
          setPhoneLoginStatus('rejected');
          clearInterval(interval);
        } else if (s === 'expired') {
          setPhoneLoginStatus('expired');
          clearInterval(interval);
        }
      } catch {
        // 404 means session not found — treat as expired
        setPhoneLoginStatus('expired');
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [phoneLoginSessionId, phoneLoginStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer for phone login
  useEffect(() => {
    if (phoneLoginStatus !== 'waiting' || phoneLoginCountdown <= 0) return;
    const timer = setTimeout(() => setPhoneLoginCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [phoneLoginCountdown, phoneLoginStatus]);

  useEffect(() => {
    if (phoneLoginCountdown <= 0 && phoneLoginStatus === 'waiting') {
      setPhoneLoginStatus('expired');
    }
  }, [phoneLoginCountdown, phoneLoginStatus]);

  const resetPhoneLogin = () => {
    setPhoneLoginSessionId(null);
    setPhoneLoginStatus(null);
    setPhoneLoginCountdown(0);
    setError('');
  };

  const handleVerifyCode = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/verify-code/`,
        { code: verificationCode, remember_device: rememberDevice, device_label: navigator.userAgent.slice(0, 80) },
        { headers: { Authorization: `Bearer ${tempAccessToken}` } }
      );

      if (response.data.success) {
        if (response.data.device_token) {
          try { localStorage.setItem(TWO_FACTOR_DEVICE_KEY, response.data.device_token); } catch (e) { /* storage blocked */ }
        }
        setShowModal(false);
        handleSuccessfulLogin(tempAccessToken);
      } else {
        setVerificationError('Invalid verification code');
      }
    } catch (error) {
      setVerificationError('Verification failed');
    }
  };

  const checkOnboardingComplete = async (accessToken, userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userData = response.data;
      return userData.onboarding_completed === true;
    } catch (error) {
      return true;
    }
  };

  const getDashboardPath = dashboardPathFor;

  const navigateAfterLogin = async (accessToken, userData) => {
    login(userData);
    // Zawsze przekieruj na dashboard - onboarding będzie obsługiwany tam
    navigate(getDashboardPath(userData.role), { replace: true });
  };

  const checkRodoConsents = async (accessToken, userData) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/rodo/consents/check_consents/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const consents = Array.isArray(response.data) ? response.data : [];
      setMissingConsents(consents);

      if (consents.length > 0) {
        setShowRodoModal(true);
      } else {
        await navigateAfterLogin(accessToken, userData);
      }
    } catch (error) {
      setError('Failed to check RODO consents');
    }
  };

  const handleSignConsent = async (consentId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/rodo/consents/sign_consent/`,
        { consent_type_id: consentId },
        { headers: { Authorization: `Bearer ${tempAccessToken}` } }
      );

      const updatedConsents = missingConsents.filter((consent) => consent.id !== consentId);
      setMissingConsents(updatedConsents);

      if (updatedConsents.length === 0 && userDataTemp) {
        setShowRodoModal(false);
        await navigateAfterLogin(tempAccessToken, userDataTemp);
      }
    } catch (error) {
      setError('Failed to sign consent');
    }
  };

  const handleSuccessfulLogin = async (accessToken) => {
    try {
      const userInfo = await axios.get(`${API_BASE_URL}/user-info/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userData = buildUserData(userInfo.data, accessToken);

      login(userData);
      setUserDataTemp(userData);
      await checkRodoConsents(accessToken, userData);
    } catch (error) {
      setError('Error fetching user info');
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowRodoModal(false);
    setUserDataTemp(null);
    setMissingConsents([]);
    setTempAccessToken('');
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      recoveryEmail: '',
    });
    navigate('/login', { replace: true });
  };

  const toggleForm = () => {
    setIsRegistering(!isRegistering);
    setIsForgotPassword(false);
    setError('');
    setSuccessMessage('');
    setFormData({ username: '', email: '', password: '', confirmPassword: '', recoveryEmail: '' });
  };

  return (
    <div className="relative w-full h-dvh bg-[#030303] flex flex-col md:flex-row overflow-hidden">
      {/* Left Section: Intro Animation Area */}
      <div className="w-full md:w-1/2 h-1/3 md:h-full flex items-center justify-center relative z-20 border-b md:border-b-0 md:border-r border-white/5 bg-[#030303]">
        <EntrigyIntro onComplete={handleIntroComplete} />
      </div>

      {/* Right Section: Login Form Area */}
      <div className="w-full md:w-1/2 h-2/3 md:h-full flex flex-col items-center relative z-20 bg-[#050505] overflow-y-auto md:overflow-hidden">
        <AnimatePresence>
          {showLogin && (
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="w-full flex justify-center px-4 my-auto py-8 md:py-0"
            >
              {/* Login Form Container */}
              <div className="w-full max-w-sm px-6 py-8 md:px-8 md:py-10 rounded-3xl bg-[#0a0a0a]/40 border border-white/5 backdrop-blur-md shadow-2xl relative overflow-hidden">
                {/* Delicate gradient accent from the top */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FE3F06]/40 to-transparent z-20 pointer-events-none" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-16 bg-[#FE3F06]/10 blur-[30px] rounded-full pointer-events-none z-0" />
                {/* Delicate gradient accent from the bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FE3F06]/40 to-transparent z-20 pointer-events-none" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-16 bg-[#FE3F06]/10 blur-[30px] rounded-full pointer-events-none z-0" />

                <div className="relative z-10">
                  {/* Header */}
                  <div className="mb-8 text-center">
                    <div className="text-white font-outfit text-xl font-semibold tracking-[0.3em] mb-4">
                      {isInviteRegister || inviteError ? 'INVITATION' : isRegistering ? 'REGISTRATION' : isForgotPassword ? 'RECOVERY' : isPhoneLogin ? 'PHONE LOGIN' : 'AUTHORIZATION'}
                    </div>
                    <div className="min-h-[2rem] flex items-center justify-center py-1">
                      <p className="text-white/40 text-[10px] sm:text-[11px] leading-relaxed px-2 font-light italic text-center">
                        {isInviteRegister
                          ? `Join ${inviteData?.company_name} — set your password to complete registration`
                          : inviteError
                          ? 'This invitation link is invalid or has expired'
                          : isRegistering
                          ? 'Create your account to access the platform'
                          : isForgotPassword
                          ? 'Enter your email to reset your password'
                          : isPhoneLogin
                          ? 'Approve login from your mobile device'
                          : 'Enter your credentials to access the control system'}
                      </p>
                    </div>
                  </div>

                  {/* Invite Error */}
                  {inviteError && (
                    <div className="space-y-5">
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-center">
                        <p className="text-red-400 text-[11px] mb-1">{inviteError}</p>
                      </div>
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => { setInviteError(null); setIsInviteRegister(false); }}
                          className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                        >
                          Back to Login
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Invite Registration Form */}
                  {isInviteRegister && !inviteError && (
                    <form onSubmit={handleInviteRegister} className="space-y-5">
                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Email</label>
                        <input
                          type="email"
                          value={inviteData?.email || ''}
                          readOnly
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/50 text-sm cursor-not-allowed"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Company</label>
                        <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/50 text-sm">
                          {inviteData?.company_name || '—'}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Role</label>
                        <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/50 text-sm">
                          {inviteData?.is_company_admin ? 'Administrator' : 'User'}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Password</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Create password"
                          required
                        />
                        <PasswordStrengthMeter password={formData.password} />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Confirm password"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        ) : (
                          <span className="uppercase tracking-[0.3em] text-[10px]">Complete Registration</span>
                        )}
                      </button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => { setIsInviteRegister(false); setInviteToken(null); setInviteData(null); }}
                          className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                        >
                          Back to Login
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Login Form */}
                  {!isInviteRegister && !inviteError && !isRegistering && !isForgotPassword && !isPhoneLogin && (
                    <form onSubmit={handleLogin} className="space-y-5">
                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Identifier</label>
                        <input
                          type="email"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="E-mail address"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center px-1">
                          <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-medium">Security Key</label>
                          <button
                            type="button"
                            onClick={() => setIsForgotPassword(true)}
                            className="text-[9px] text-white/20 hover:text-white transition-colors uppercase tracking-widest"
                          >
                            Forgot?
                          </button>
                        </div>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Password"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        ) : (
                          <span className="uppercase tracking-[0.3em] text-[10px]">Authenticate</span>
                        )}
                      </button>

                      {/* Divider */}
                      <div className="flex items-center space-x-3 py-1">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-[9px] text-white/20 uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>

                      {/* Phone Login Button */}
                      <button
                        type="button"
                        onClick={() => { setIsPhoneLogin(true); setError(''); setSuccessMessage(''); }}
                        className="w-full py-3.5 bg-white/5 border border-white/10 text-white/70 rounded-xl hover:bg-white/10 hover:text-white transition-all flex items-center justify-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                        </svg>
                        <span className="uppercase tracking-[0.2em] text-[10px]">Login via Phone</span>
                      </button>

                      {ssoAvailable && (
                        <button
                          type="button"
                          onClick={startMicrosoftSignIn}
                          disabled={ssoBusy}
                          className="w-full py-3.5 bg-white/5 border border-white/10 text-white/70 rounded-xl hover:bg-white/10 hover:text-white transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 21 21" aria-hidden="true">
                            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                          </svg>
                          <span className="uppercase tracking-[0.2em] text-[10px]">
                            {ssoBusy ? 'Redirecting…' : 'Sign in with Microsoft'}
                          </span>
                        </button>
                      )}
                    </form>
                  )}

                  {/* Phone Login Flow */}
                  {isPhoneLogin && !isInviteRegister && !inviteError && (
                    <div className="space-y-5">
                      {!phoneLoginStatus && (
                        <form onSubmit={handlePhoneLoginRequest} className="space-y-5">
                          <div className="space-y-2">
                            <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Your Email</label>
                            <input
                              type="email"
                              value={phoneLoginEmail}
                              onChange={(e) => { setPhoneLoginEmail(e.target.value); setError(''); }}
                              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                              placeholder="E-mail address"
                              required
                            />
                          </div>
                          <p className="text-[10px] text-white/30 px-1 leading-relaxed">
                            A push notification will be sent to your phone. Open the Winduno app and approve the login request.
                          </p>
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          >
                            {isLoading ? (
                              <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                            ) : (
                              <span className="uppercase tracking-[0.3em] text-[10px]">Send Request</span>
                            )}
                          </button>
                          <div className="text-center">
                            <button
                              type="button"
                              onClick={() => { setIsPhoneLogin(false); resetPhoneLogin(); }}
                              className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                            >
                              Back to login
                            </button>
                          </div>
                        </form>
                      )}

                      {phoneLoginStatus === 'waiting' && (
                        <div className="text-center space-y-6">
                          <div className="flex justify-center">
                            <div className="relative">
                              <div className="w-16 h-16 border-2 border-white/10 rounded-full flex items-center justify-center">
                                <svg className="w-7 h-7 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                                </svg>
                              </div>
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FE3F06] rounded-full flex items-center justify-center animate-pulse">
                                <div className="w-2 h-2 bg-white rounded-full" />
                              </div>
                            </div>
                          </div>
                          <div>
                            <p className="text-white/70 text-sm mb-1">Waiting for approval...</p>
                            <p className="text-white/30 text-[10px]">
                              Check your phone and tap <span className="text-emerald-400">Approve</span> in the Winduno app
                            </p>
                          </div>
                          <div className="text-white/20 text-[10px] font-mono">
                            {Math.floor(phoneLoginCountdown / 60)}:{String(phoneLoginCountdown % 60).padStart(2, '0')}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setIsPhoneLogin(false); resetPhoneLogin(); }}
                            className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {(phoneLoginStatus === 'rejected' || phoneLoginStatus === 'expired') && (
                        <div className="text-center space-y-5">
                          <div className="flex justify-center">
                            <div className="w-16 h-16 border-2 border-red-500/30 rounded-full flex items-center justify-center">
                              <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                          </div>
                          <p className="text-white/70 text-sm">
                            {phoneLoginStatus === 'rejected' ? 'Login request was rejected' : 'Login request expired'}
                          </p>
                          <div className="flex flex-col space-y-3">
                            <button
                              type="button"
                              onClick={resetPhoneLogin}
                              className="w-full py-3.5 bg-white/5 border border-white/10 text-white/70 rounded-xl hover:bg-white/10 transition-all uppercase tracking-[0.2em] text-[10px]"
                            >
                              Try Again
                            </button>
                            <button
                              type="button"
                              onClick={() => { setIsPhoneLogin(false); resetPhoneLogin(); }}
                              className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                            >
                              Back to login
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Forgot Password Form */}
                  {isForgotPassword && (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Email Address</label>
                        <input
                          type="email"
                          name="recoveryEmail"
                          value={formData.recoveryEmail}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Enter your email"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        ) : (
                          <span className="uppercase tracking-[0.3em] text-[10px]">Recover</span>
                        )}
                      </button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => setIsForgotPassword(false)}
                          className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                        >
                          Back to login
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Registration Form */}
                  {isRegistering && (
                    <form onSubmit={handleRegistration} className="space-y-5">
                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="E-mail address"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Password</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Create password"
                          required
                        />
                        <PasswordStrengthMeter password={formData.password} />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] text-white/30 uppercase tracking-[0.2em] px-1 font-medium">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 transition-all placeholder:text-white/20 text-sm"
                          placeholder="Confirm password"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        ) : (
                          <span className="uppercase tracking-[0.3em] text-[10px]">Register</span>
                        )}
                      </button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={toggleForm}
                          className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-widest"
                        >
                          Back to login
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Error/Success Messages */}
                  {(error || successMessage) && (
                    <div
                      className={`mt-5 p-3 rounded-xl text-center text-[11px] ${
                        error
                          ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      }`}
                    >
                      {error || successMessage}
                    </div>
                  )}

                  {/* Create Account Link */}
                  {!isInviteRegister && !inviteError && !isRegistering && !isForgotPassword && !isPhoneLogin && (
                    <>
                      <p className="mt-6 text-center text-[9px] text-white/20 uppercase tracking-[0.3em] font-light">
                        Access Request?{' '}
                        <button
                          type="button"
                          onClick={toggleForm}
                          className="text-white/60 hover:text-white transition-all"
                        >
                          Create Account
                        </button>
                      </p>
                      <p className="mt-3 text-center text-[9px] text-white/20 uppercase tracking-[0.3em] font-light">
                        Forgot your password?{' '}
                        <button
                          type="button"
                          onClick={() => setIsForgotPassword(true)}
                          className="text-white/60 hover:text-white transition-all"
                        >
                          Reset Password
                        </button>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Placeholder before login appears */}
        {!showLogin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.1 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-px h-32 bg-gradient-to-b from-transparent via-emerald-500 to-transparent" />
          </motion.div>
        )}

        {/* Bottom Links - App Store, Search */}
        {showLogin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative shrink-0 pb-8 md:pb-0 md:absolute md:bottom-8 md:left-0 md:right-0 flex flex-wrap items-center justify-center gap-2 px-4"
          >
            <a
              href="https://play.google.com/store/apps/details?id=energy.ventro"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-transparent border border-white/10 text-white/60 hover:text-white/80 hover:border-white/40 transition-all text-xs"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <span>Google Play</span>
            </a>
            <a
              href="https://apps.apple.com/pl/app/ventro/id6753670953?platform=iphone"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-transparent border border-white/10 text-white/60 hover:text-white/80 hover:border-white/40 transition-all text-xs"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05,20.28C16.1,21.23 15.17,20.25 14.24,19.95C13.26,19.64 12.33,19.62 11.3,19.95C10,20.39 9.32,20.21 8.54,19.28C4.27,14.75 4.9,8.05 9.66,7.79C10.85,7.85 11.65,8.45 12.34,8.5C13.5,8.26 14.62,7.64 15.85,7.72C17.35,7.83 18.46,8.39 19.17,9.43C16.33,11.09 17,15.19 19.78,16.29C19.23,17.78 18.54,19.24 17.04,20.3M12.21,7.66C12.06,5.5 13.87,3.75 15.91,3.58C16.23,6.05 13.63,7.87 12.21,7.66Z"/>
              </svg>
              <span>App Store</span>
            </a>
            <button
              onClick={() => navigate('/search')}
              className="flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-transparent border border-white/10 text-white/60 hover:text-white/80 hover:border-white/40 transition-all text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Search Users</span>
            </button>
          </motion.div>
        )}
      </div>

      {/* Shared Environmental Background Accents */}
      <div className="absolute inset-0 pointer-events-none z-10">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-emerald-900/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-teal-900/10 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '1s' }} />

        {/* Wind Stream Particles Effect across both panels */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ x: '-10%', y: `${10 + i * 12}%`, opacity: 0 }}
              animate={{ x: '110%', opacity: [0, 1, 0] }}
              transition={{
                duration: 10 + i * 3,
                repeat: Infinity,
                delay: i * 2,
                ease: "linear"
              }}
              className="absolute h-[1px] w-96 bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
            />
          ))}
        </div>
      </div>

      {/* Verification Modal */}
      {showModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-md w-full mx-4 p-6"
          >
            <h3 className="text-xl font-outfit font-semibold text-white mb-2 tracking-wide">Verify Your Identity</h3>
            <p className="text-white/50 text-sm mb-6">Enter the verification code sent to your email.</p>

            <input
              type="text"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-1 focus:ring-[#FE3F06]/30 mb-4 text-center text-lg tracking-[0.5em]"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />

            <label className="flex items-center justify-center gap-2 text-white/70 text-sm mb-4">
              <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
              Remember this browser
            </label>

            {verificationError && (
              <p className="text-red-400 text-sm mb-4 text-center">{verificationError}</p>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                className="flex-1 py-3 bg-white/5 border border-white/10 text-white/70 rounded-xl hover:bg-white/10 transition-all"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 py-3 bg-white text-black font-bold rounded-xl hover:bg-neutral-200 transition-all"
                onClick={handleVerifyCode}
              >
                Verify
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* RODO Consents Modal */}
      {showRodoModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-xl font-outfit font-semibold text-white mb-2 tracking-wide">Required RODO Consents</h3>
            <p className="text-white/50 text-sm mb-6">Please accept the following consents to proceed.</p>

            <div className="space-y-4 mb-6">
              {missingConsents.map((consent) => (
                <div key={consent.id} className="border border-white/10 rounded-xl p-4 bg-white/5">
                  <h4 className="font-medium text-white mb-2">{consent.name}</h4>
                  <div className="text-sm text-white/50 mb-4 flex space-x-4">
                    <button
                      onClick={() => {
                        const newWindow = window.open('', '_blank');
                        if (newWindow) {
                          newWindow.document.write(`
                            <!DOCTYPE html>
                            <html lang="en">
                            <head>
                              <meta charset="UTF-8">
                              <meta name="viewport" content="width=device-width, initial-scale=1.0">
                              <title>Consent Document: ${consent.name}</title>
                              <style>
                                body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; background: #0a0a0a; color: #fff; }
                                h1 { color: #fff; }
                                pre { white-space: pre-wrap; background: #111; padding: 15px; border-radius: 8px; border: 1px solid #333; }
                              </style>
                            </head>
                            <body>
                              <h1>Consent Document: ${consent.name}</h1>
                              <pre>${consent.content || 'No content available'}</pre>
                            </body>
                            </html>
                          `);
                          newWindow.document.close();
                        } else {
                          setError('Failed to open new window. Please allow pop-ups.');
                        }
                      }}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors text-xs"
                    >
                      View document
                    </button>
                    <a
                      href={consent.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 transition-colors text-xs"
                    >
                      Download document
                    </a>
                  </div>
                  <button
                    onClick={() => handleSignConsent(consent.id)}
                    className="w-full py-2 bg-white text-black font-bold rounded-lg hover:bg-neutral-200 transition-all text-sm"
                  >
                    Accept
                  </button>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-white/5 border border-white/10 text-white/70 rounded-lg hover:bg-white/10 transition-all"
              >
                Logout
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
